import classNames from "classnames";
import throttle from "lodash/throttle";
import moment from "moment";
import { FC, useCallback, useEffect, useState } from "react";
import { Transition } from "react-spring/renderprops.cjs";
import { toast } from "react-toastify";
import { useBoolean } from "usehooks-ts";
import { Zoom } from "../../App";
import {
    AutomaticOrdering,
    InventoryItem,
    Order,
    OrderItem,
    OrderItemInput,
    OrderItemStatus,
    OrderNewItemInput,
    useUpdateOrderItemsMutation,
} from "../../Generated/graphql";
import { getShipmentStatus } from "../../Utils/shipmentStatus";

import { AddNewItems } from "../AddNewItems/AddNewItems";
import { Icon } from "../Icon/Icon";
import { OrderComment } from "../OrderComment/OrderComment";
import { OrderDetails } from "../OrderDetails/OrderDetails";
import { ScanItemInModal } from "../ScanItemInModal/ScanItemInModal";
import "./ActiveOrders.css";

export interface IUserOrderInput {
  productNumber: string;
  taken: number;
  excluded: boolean;
  price: number;
}

type ActiveOrdersProps = {
  order: Order;
  inventory: InventoryItem[];
};

const sortedOrderlines = (lines: OrderItem[]) => {

  if (!lines || lines?.length === 0) {
    return [];
  }

  // We want items with status === 'completed' at the bottom, with status 'backorder' in the middle, and status 'active' at the top
  const sortedLines = [...lines].sort((a, b) => {

    if (
      a.status === OrderItemStatus.Completed &&
      b.status !== OrderItemStatus.Completed
    ) {
      return 1;
    } else if (
      a.status !== OrderItemStatus.Completed &&
      b.status === OrderItemStatus.Completed
    ) {
      return -1;
    } else if (
      a.status === OrderItemStatus.Backorder &&
      b.status === OrderItemStatus.Active
    ) {
      return 1;
    } else if (
      a.status === OrderItemStatus.Active &&
      b.status === OrderItemStatus.Backorder
    ) {
      return -1;
    } else {
      return 0;
    }
  });
  return sortedLines;
};



export const ActiveOrders: FC<ActiveOrdersProps> = ({ order, inventory }) => {
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [newItems, setNewItems] = useState<OrderNewItemInput[]>([]);
  const [updateItems, setUpdateItems] = useState<OrderItemInput[]>([]);
  const [currentOrder, setCurrentOrder] = useState<Order>(order);
  const [orderLines, setOrderlines] = useState<OrderItem[]>(
    sortedOrderlines(order.orderLines)
  );
  const [warningMessage, setWarningMessage] = useState<string | undefined>(
    undefined
  );
  const [showScanModal, setShowScanModal] = useState<boolean>(false);
  const [showOrder, setShowOrder] = useState<boolean>(false);
  const [showComments, setShowComments] = useState<boolean>(false);
  const {
    value: showNewItemsModal,
    toggle: toggleShowNewItemsModal,
  } = useBoolean(false);

  const [addingToStock, setAddingToStock] = useState<boolean>(false);
  const [pendingOperations, setPendingOperations] = useState<Set<string>>(new Set());
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());
  // Track which order line IDs have already been submitted to the server
  // to prevent sending duplicates in rapid successive mutations
  const [submittedItemIds, setSubmittedItemIds] = useState<Set<string>>(new Set());

  const [updateOrderItems] = useUpdateOrderItemsMutation();

  useEffect(() => {
    setCurrentOrder(order);
    setOrderlines(sortedOrderlines(order.orderLines));
    
    // Clear loading state when polling detects completed operations
    if (addingToStock && pendingOperations.size > 0) {
      const allCompleted = Array.from(pendingOperations).every(itemId => {
        const item = order.orderLines.find(line => line.id === itemId);
        return item?.status === OrderItemStatus.Completed;
      });
      
      if (allCompleted) {
        setAddingToStock(false);
        setPendingOperations(new Set());
      }
    }
    
    // Clear individual item loading when items complete
    if (loadingItems.size > 0) {
      const completedItems = Array.from(loadingItems).filter(itemId => {
        const item = order.orderLines.find(line => line.id === itemId);
        return item?.status === OrderItemStatus.Completed;
      });
      
      if (completedItems.length > 0) {
        setLoadingItems(prev => {
          const newSet = new Set(prev);
          completedItems.forEach(itemId => newSet.delete(itemId));
          return newSet;
        });
      }
    }
  }, [order, order.orderLines, addingToStock, pendingOperations, loadingItems]);

  useEffect(() => {
    updateWindowDimensions();
    window.addEventListener("resize", updateWindowDimensions);

    return () => {
      window.removeEventListener("resize", updateWindowDimensions);
    };
  }, []);

  const updateWindowDimensions = () => {
    setWindowWidth(window.innerWidth);
  };

  const changeItemAmount = (productNumber: string, amount: number) => {
    const index = orderLines.findIndex(line => line.productNumber === productNumber);
    if (index !== -1) {
      const newOrderLines = [...orderLines];
      newOrderLines[index] = { ...newOrderLines[index], amount };
      setOrderlines(newOrderLines);
    }
  };

  const changeExcludedStateForProduct = (
    productNumber: string,
    excluded: boolean
  ) => {
    const index = orderLines.findIndex(line => line.productNumber === productNumber);
    if (index !== -1) {
      const newOrderLines = [...orderLines];
      newOrderLines[index] = { ...newOrderLines[index], excluded };
      setOrderlines(newOrderLines);
    }
  };

  const toggleScan = () => setShowScanModal((prev) => !prev);

  const handleUpdateOrderItems = useCallback(async (
    newItems: OrderNewItemInput[],
    updateItems: OrderItemInput[],
    warningMessage?: string
  ) => {
    if(addingToStock) {
      return
    }

    // Filter out items that have already been submitted to prevent duplicates
    const filteredUpdateItems = updateItems.filter(item => !submittedItemIds.has(item.id));
    const filteredNewItems = newItems.filter(item => !submittedItemIds.has(item.id));

    if (filteredUpdateItems.length === 0 && filteredNewItems.length === 0) {
      // All items already submitted, nothing to do
      setUpdateItems([]);
      setNewItems([]);
      return;
    }

    // Only show bulk overlay for multiple items
    const isBulkOperation = (filteredNewItems.length + filteredUpdateItems.length) > 1;
    if (isBulkOperation) {
      setAddingToStock(true);
    }

    // Track submitted IDs immediately to prevent re-submission during concurrent calls
    const allSubmittedIds = [...filteredUpdateItems.map(item => item.id), ...filteredNewItems.map(item => item.id)];
    setSubmittedItemIds(prev => {
      const next = new Set(prev);
      allSubmittedIds.forEach(id => next.add(id));
      return next;
    });

    await updateOrderItems({
      variables: {
        items: filteredUpdateItems,
        newItems: filteredNewItems,
        orderId: order.id,
        withWarningMessage: warningMessage,
      },
    })
      .then((result) => {
        toast("Ordre opdateret", {
          className: "u-toast-success",
          progressClassName: "u-toast-success-bar",
          transition: Zoom,
        });
        
        // Track which items are being processed (only for bulk operations)
        if (isBulkOperation) {
          setPendingOperations(new Set(allSubmittedIds));
        }
        
        setUpdateItems([]);
        setNewItems([]);
        // Don't clear addingToStock here - let polling handle it
      })
      .catch((error) => {
        const errorMessage = error?.message || "Fejl ved opdatering";
        toast(errorMessage, {
          className: "u-toast-error",
          progressClassName: "u-toast-error-bar",
        });
        // On failure, remove from submitted so they can be retried
        setSubmittedItemIds(prev => {
          const next = new Set(prev);
          allSubmittedIds.forEach(id => next.delete(id));
          return next;
        });
        if (isBulkOperation) {
          setAddingToStock(false);
        }
      });
  }, [addingToStock, submittedItemIds, updateOrderItems, order.id, setAddingToStock, setPendingOperations, setUpdateItems, setNewItems]);

  const handleItemAction = useCallback((itemId: string, included: boolean) => {
    const item = orderLines.find(line => line.id === itemId);
    if (!item) return;

    // Prevent adding items with 0 amount to stock
    if (included && item.amount === 0) {
      toast("Kan ikke tilføje vare med antal 0 til lager. Enten udelad varen eller ret antallet.", {
        className: "u-toast-error",
        progressClassName: "u-toast-error-bar",
        transition: Zoom,
      });
      return;
    }

    // Set individual item loading
    setLoadingItems(prev => new Set(prev).add(itemId));

    const orderItemInput: OrderItemInput = {
      id: item.id,
      productNumber: item.productNumber,
      excluded: !included,
      price: item.price,
      taken: item.amount,
    };

    // If excluding, just exclude directly regardless of whether item is in inventory
    if (!included) {
      handleUpdateOrderItems([], [orderItemInput]);
      return;
    }

    const existingInventoryItem = inventory.find(
      (invItem) => invItem.productNumber === item.productNumber
    );

    if (!existingInventoryItem) {
      setNewItems([{
        ...orderItemInput,
        automaticOrder: AutomaticOrdering.Active,
        maximum: 1,
        minimum: 1,
      }]);
      toggleShowNewItemsModal();
    } else {
      handleUpdateOrderItems([], [orderItemInput]);
    }
  }, [orderLines, inventory, handleUpdateOrderItems, toggleShowNewItemsModal]);



  const getItemDisplayState = useCallback((item: OrderItem) => {
    return {
      status: item.status,
      excluded: item.excluded,
      loading: loadingItems.has(item.id),
      hasZeroAmount: item.amount === 0
    };
  }, [loadingItems]);

  // Calls the server to update a single item
  // This shall also go through the whole "new item" process if needed
  const handleAddItem = (item: OrderItemInput) => {
    if (item.excluded) {
      handleUpdateOrderItems([], [item]);
      return;
    }

    // Check if the item is a new, or existing item
    const existingInventoryItem = inventory.find(
      (invItem) => invItem.productNumber === item.productNumber
    );

    // If new, just set it in the "new" state
    // and return
    if (!existingInventoryItem) {
      setNewItems([
        {
          ...item,
          automaticOrder: AutomaticOrdering.Active,
          maximum: 1,
          minimum: 1,
        },
      ]);

      toggleShowNewItemsModal();
      return;
    }

    // If it's an update item - Just run the update, with an empty "new items" array
    handleUpdateOrderItems([], [item]);
  };

  const handleRemoveItems = (productNumbers: string[]) => {
    // Remove any item in product numbers from either new or update items
    const newProductNumbers = productNumbers.filter(
      (number) => !inventory.find((item) => item.productNumber === number)
    );
    const existingProductNumbers = productNumbers.filter((number) =>
      inventory.find((item) => item.productNumber === number)
    );

    setNewItems((prev) =>
      prev.filter((item) => !newProductNumbers.includes(item.productNumber))
    );
    setUpdateItems((prev) =>
      prev.filter(
        (item) => !existingProductNumbers.includes(item.productNumber)
      )
    );
  };

  const handleSelectItems = (productNumbers: string[]) => {
    // Add any item in product numbers from either new or update items

    // This includes existing items and excluded items, but NOT already completed items
    const existingAndExcludedProductNumbers = productNumbers.filter(
      (productNumber) => {
        const orderItem = orderLines.find(
          (line) =>
            line.productNumber === productNumber &&
            line.status === OrderItemStatus.Active // Only active items can be processed
        );

        const inventoryItem = inventory.find(
          (item) => item.productNumber === productNumber
        );

        return (inventoryItem || orderItem?.excluded) && orderItem; // Must be active
      }
    );

    const newProductNumbers = productNumbers.filter((productNumber) => {
      const orderItem = orderLines.find(
        (line) => line.productNumber === productNumber
      );
      const inventoryItem = inventory.find(
        (item) => item.productNumber === productNumber
      );

      return !inventoryItem && !orderItem?.excluded && orderItem?.status === OrderItemStatus.Active;
    });

    const existingOrderItems: OrderItemInput[] = orderLines
      .filter((line) =>
        existingAndExcludedProductNumbers.includes(line.productNumber)
      )
      .map((item) => ({
        id: item.id,
        excluded: item.excluded,
        price: item.price,
        productNumber: item.productNumber,
        taken: item.amount,
      }));

    const newOrderItems = orderLines
      .filter((line) => newProductNumbers.includes(line.productNumber))
      .map((item) => ({
        id: item.id,
        automaticOrder: AutomaticOrdering.Active,
        excluded: item.excluded,
        maximum: 1,
        minimum: 1,
        price: item.price,
        productNumber: item.productNumber,
        taken: item.amount,
      }));

    setNewItems((prev) => [...prev, ...newOrderItems]);
    setUpdateItems((prev) => [...prev, ...existingOrderItems]);
  };

  const onNewItemsModalClose = (formattedNewItems: OrderNewItemInput[]) => {
    if(addingToStock) {
      return
    }

      if (formattedNewItems.length === 0) {
        toggleShowNewItemsModal();
        setNewItems([]);
        return;
      }
  
      handleUpdateOrderItems(formattedNewItems, updateItems, warningMessage).then(
        () => {
          toggleShowNewItemsModal();
        }
      );
  };

  const onTriggerUpdateOrderItems = () => {
    if(addingToStock) {
      return
    }

    if (newItems.length === 0 && updateItems.length === 0) {
      return;
    }
    if (newItems.length === 0) {      
      // No "new" items, updating items without showing modal first
      handleUpdateOrderItems([], updateItems)
      return;
    }
    // We have new items, showing modal
    // so the user can setup their new items
    toggleShowNewItemsModal();
  };

  const debouncedTriggerUpdateOrderItems = throttle(onTriggerUpdateOrderItems, 100);

  const onAddScannedItems = (
    items: OrderItemInput[],
    warningMessage?: string
  ) => {
    if(addingToStock) {
      return
    }

    // Add any item in product numbers from either new or update items
    const existingOrderItems = items.filter((item) =>
      inventory.find(
        (invItem) =>
          invItem.productNumber === item.productNumber || item.excluded
      )
    );

    const newOrderItems = items
      .filter(
        (item) =>
          !inventory.find(
            (invItems) =>
              invItems.productNumber === item.productNumber && !item.excluded
          )
      )
      .map((item) => ({
        id: item.id,
        automaticOrder: AutomaticOrdering.Active,
        excluded: item.excluded,
        maximum: 1,
        minimum: 1,
        price: item.price,
        productNumber: item.productNumber,
        taken: item.taken,
      }));

    // If we have new items, set them in state, so we can do the magic
    if (newOrderItems.length > 0) {
      setWarningMessage(warningMessage);
      setNewItems(newOrderItems);
      setUpdateItems(existingOrderItems);
      toggleShowNewItemsModal();
      return;
    }

    // In case we have no "new items", just go ahead an update
    handleUpdateOrderItems([], existingOrderItems, warningMessage);
  };

  const borderStyle = ["ActiveOrders", "ActiveOrders__not-expanded"];
  const borderStyleExpanded = ["ActiveOrders", "ActiveOrders__expanded"];

  return (
    <div
      className={classNames("ActiveOrder__order", {
        "ActiveOrder__order--expanded": showOrder,
      })}
    >
      <ScanItemInModal
        items={currentOrder.orderLines}
        orderNumber={currentOrder.id}
        toggleScan={toggleScan}
        addScannedItems={onAddScannedItems}
        hideMe={() => setShowScanModal(false)}
        open={showScanModal}
      />

      <Transition
        items={showNewItemsModal}
        config={{
          tension: 110,
          friction: 20,
          precision: 0.1,
          velocity: 14,
        }}
        from={{ opacity: 0 }}
        enter={{ opacity: 1 }}
        leave={{ opacity: 0 }}
      >
        {(showAddNewItemsModal) =>
          showAddNewItemsModal &&
          ((props) => (
            <AddNewItems
              key={currentOrder.id}
              items={newItems}
              orderId={currentOrder.id}
              close={onNewItemsModalClose}
              style={props}
            />
          ))
        }
      </Transition>

      <div
        className={
          showOrder ? borderStyleExpanded.join(" ") : borderStyle.join(" ")
        }
        onClick={(e) => {
          if (
            (e.target as HTMLElement).closest('a') ||
            (e.target as HTMLElement).closest('#ActiveOrder__commentclicker') ||
            showComments
          ) {
            return;
          }
          setShowOrder((prev) => !prev);
        }}
      >
        <div className="ActiveOrders__icon">
          <Icon name="cartListview" width={28} className="Icon__center" />
        </div>
        <div className="ActiveOrders__order">
          <div className="ActiveOrders__firstPart">
            <div className="ActiveOrders__order-number">Ordre {currentOrder.id}</div>
            {currentOrder.deliveryId ? (<div className="ActiveOrders__delivery-number">
              Leverance {currentOrder.deliveryId}
            </div>) : (<></>)}
            {windowWidth > 1024 ? (
              <div className="ActiveOrders__order-date">
                Bestilt{" "}
                {moment(currentOrder.orderDate, "YYYYMMDD").format("DD[.] MMMM YYYY")}
              </div>
            ) : (
              <div className="ActiveOrders__order-date">
                {moment(currentOrder.orderDate, "YYYYMMDD").format(
                  "DD[.] MMM[.] YYYY"
                )}
              </div>
            )}
          </div>
          <div className="ActiveOrders__secondPart">
            <div className="ActiveOrders__order-amount">
              {currentOrder.orderLines.length} varer i alt
            </div>
            <div className="ActiveOrders__order-from">
              Bestilt fra{" "}
              {currentOrder.orderedFrom === 2
                ? "lagerstyring"
                : currentOrder.orderedFrom === 1
                  ? "webshop"
                  : "internt salg"}
            </div>
          </div>
          <div className="ActiveOrders__thirdPart">
            {(() => {
              const shipmentInfo = getShipmentStatus(currentOrder);
              if (shipmentInfo.status === "pending") return null;
              return (
                <div className="ActiveOrders__shipment-status">
                  <div className="ActiveOrders__shipment-status-icon">
                    <Icon name={shipmentInfo.icon} width={15} />
                  </div>
                  <div className="ActiveOrders__shipment-status-text">
                    {shipmentInfo.label}
                  </div>
                  {shipmentInfo.trackingUrl && (
                    <a
                      className="ActiveOrders__tracking-link"
                      href={shipmentInfo.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Spor
                    </a>
                  )}
                </div>
              );
            })()}
            {currentOrder &&
              currentOrder.comments &&
              Array.isArray(currentOrder.comments) &&
              currentOrder.comments.length > 0 && (
                <div
                  id="ActiveOrder__commentclicker"
                  className="ActiveOrder__comment-clicker"
                  onClick={() => setShowComments((prev) => !prev)}
                >
                  <OrderComment
                    comments={currentOrder.comments}
                    displayComments={showComments}
                    orderId={currentOrder.id}
                  />
                </div>
              )}
          </div>
          <div className="ActiveOrders__see-order u-cursor-pointer">
            <div className="ActiveOrders__see-order-text">Se bestilling</div>
            <div
              style={
                showOrder
                  ? {
                    transform: "translate(11px, -1px)",
                    transition: "transform 100ms ease-out",
                  }
                  : {
                    transform: "translate(11px, 1px) rotate(-90deg)",
                    transition: "transform 100ms ease-out",
                  }
              }
            >
              <Icon name="arrowDown" width={11} />
            </div>
          </div>


        </div>
      </div>
      {showOrder && (
        <OrderDetails
          showMe={showOrder}
          orderLines={orderLines}
          openScan={toggleScan}
          windowWidth={windowWidth}
          changeItemAmount={changeItemAmount}
          addItem={handleAddItem}
          addingToStock={addingToStock}
          removeItems={handleRemoveItems}
          selectItems={handleSelectItems}
          selectedItems={[...newItems, ...updateItems].map(
            (item) => item.productNumber
          )}
          triggerUpdateOrderItems={debouncedTriggerUpdateOrderItems}
          changeExcludedState={changeExcludedStateForProduct}
          getItemDisplayState={getItemDisplayState}
          handleItemAction={handleItemAction}
        />
      )}
    </div>
  );
};
